import { calculateCardCompletion } from "@shared/field-definitions";
import type { WizardSlice } from "./types";
import type { StoryGeneralData, StoryEnvironmentData, StorySocialData, StoryGovernanceData } from "@/contexts/wizard/types";

/**
 * Sustainability Story slice state
 */
export interface SustainabilityStorySliceState {
  storyGeneralData: StoryGeneralData;
  storyEnvironmentData: StoryEnvironmentData;
  storySocialData: StorySocialData;
  storyGovernanceData: StoryGovernanceData;
}

/**
 * API response structure for sustainability story data
 */
export interface SustainabilityStoryApiData {
  generalData?: StoryGeneralData;
  environmentData?: StoryEnvironmentData;
  socialData?: StorySocialData;
  governanceData?: StoryGovernanceData;
}

/**
 * State setters for sustainability story slice
 */
export interface SustainabilityStoryStateSetter {
  setStoryGeneralData: (data: StoryGeneralData) => void;
  setStoryEnvironmentData: (data: StoryEnvironmentData) => void;
  setStorySocialData: (data: StorySocialData) => void;
  setStoryGovernanceData: (data: StoryGovernanceData) => void;
}

// ==================== API FUNCTIONS ====================

async function fetchSustainabilityStory(assessmentId: string): Promise<SustainabilityStoryApiData> {
  const response = await fetch(`/api/assessments/${assessmentId}/sustainability-story`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch sustainability story data");
  return response.json();
}

async function saveSustainabilityStory(assessmentId: string, payload: unknown) {
  return fetch(`/api/assessments/${assessmentId}/sustainability-story`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

// ==================== HYDRATION MAPPER ====================

const defaultStoryGeneralData: StoryGeneralData = {
  coverImageUrl: "",
  leadershipSignedBy: "",
  leadershipTitle: "",
  leadershipSignatureDate: "",
  leadershipPhotoUrl: "",
  leadershipIntroduction: "",
  leadershipKeyWins: "",
  leadershipAcknowledgements: "",
  leadershipLookingAhead: "",
  basisReportScope: "",
  basisLimitations: "",
  basisCertifications: "",
  practicesSustainabilityManagement: "",
  practicesConcretePractices: "",
  practicesTopInitiatives: "",
  strategyHotspots: "",
  strategyGoals: "",
  strategyPriorities: "",
  strategyFlagshipInitiatives: "",
  strategyChallenges: "",
};

const defaultStoryEnvironmentData: StoryEnvironmentData = {
  b3EmissionsDrivers: "",
  b3ReductionActions: "",
  b3NextPriorities: "",
  b3DataLimitations: "",
  b4MonitoringProcess: "",
  b4PlannedImprovements: "",
  b5MaterialityExplanation: "",
  b5ValueChainImpacts: "",
  b5ActionsToReduce: "",
  b6WaterUseDescription: "",
  b6WaterIssues: "",
  b6WaterImprovements: "",
  b7WasteManagement: "",
  b7HazardousWaste: "",
  b7CircularityImprovements: "",
  c3Targets: [],
  c4PhysicalRisks: "",
  c4TransitionRisks: "",
  c4RiskAssessment: "",
  c4AdaptationActions: "",
};

const defaultStorySocialData: StorySocialData = {
  // B8 Workforce
  b8WorkforceProfile: "",
  b8WorkforcePriorities: "",
  b8EmployeeSupportActions: "",
  // B9 Health & Safety
  b9HealthSafetyApproach: "",
  b9PreventionMeasures: "",
  b9IssuesImprovements: "",
  // B10 Remuneration/Training
  b10FairPayTrainingApproach: "",
  b10TrainingPriorities: "",
  b10NextYearImprovements: "",
  // C5 Additional Workforce & Diversity
  c5DiversityInclusionPolicies: "",
  c5ContractorReliance: "",
  c5ContractorManagement: "",
  c5DataLimitations: "",
  // C6 Human Rights Policies
  c6HumanRightsApproach: "",
  c6HumanRightsRisks: "",
  c6ControlsInPlace: "",
  // C7 Severe Incidents
  c7WorkforceIncidentsNarrative: "",
  c7ValueChainIncidentsNarrative: "",
};

const defaultStoryGovernanceData: StoryGovernanceData = {
  b11BusinessConductApproach: "",
  b11MisconductPrevention: "",
  b11NotableUpdates: "",
  c8SensitiveSectors: "",
  c8Exclusions: "",
  c8ExclusionsPractice: "",
  c9GovernanceStructure: "",
  c9DiversityInclusion: "",
  c9ImprovementsGoals: "",
};

function hydrateSustainabilityStoryState(apiData: SustainabilityStoryApiData, setter: SustainabilityStoryStateSetter): void {
  setter.setStoryGeneralData(apiData.generalData || defaultStoryGeneralData);
  setter.setStoryEnvironmentData(apiData.environmentData || defaultStoryEnvironmentData);
  setter.setStorySocialData(apiData.socialData || defaultStorySocialData);
  setter.setStoryGovernanceData(apiData.governanceData || defaultStoryGovernanceData);
}

// ==================== SIGNATURE & DIRTY TRACKING ====================

function computeSignature(state: SustainabilityStorySliceState): string {
  const payload = {
    generalData: state.storyGeneralData,
    environmentData: state.storyEnvironmentData,
    socialData: state.storySocialData,
    governanceData: state.storyGovernanceData,
  };
  return JSON.stringify(payload);
}

function isDirty(state: SustainabilityStorySliceState, baselineSignature: string): boolean {
  return computeSignature(state) !== baselineSignature;
}

// ==================== SAVE ====================

async function saveSlice(assessmentId: string, state: SustainabilityStorySliceState): Promise<void> {
  const payload = {
    generalData: state.storyGeneralData,
    generalCompletenessPercent: calculateCardCompletion('storyGeneral', state.storyGeneralData),
    environmentData: state.storyEnvironmentData,
    environmentCompletenessPercent: calculateCardCompletion('storyEnvironment', state.storyEnvironmentData),
    socialData: state.storySocialData,
    socialCompletenessPercent: calculateCardCompletion('storySocial', state.storySocialData),
    governanceData: state.storyGovernanceData,
    governanceCompletenessPercent: calculateCardCompletion('storyGovernance', state.storyGovernanceData),
  };

  const response = await saveSustainabilityStory(assessmentId, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to save sustainability story data" }));
    throw new Error(error.error || "Failed to save sustainability story data");
  }
}

// ==================== COMPLETION DATA ====================

function getCompletionData(state: SustainabilityStorySliceState) {
  return {
    generalData: state.storyGeneralData,
    environmentData: state.storyEnvironmentData,
    socialData: state.storySocialData,
    governanceData: state.storyGovernanceData,
  };
}

// ==================== SLICE EXPORT ====================

export const sustainabilityStorySlice: WizardSlice<SustainabilityStorySliceState, SustainabilityStoryApiData, SustainabilityStoryStateSetter> = {
  queryKey: (assessmentId: string) => ["sustainability-story", assessmentId],
  fetch: fetchSustainabilityStory,
  hydrate: hydrateSustainabilityStoryState,
  isDirty: isDirty,
  computeSignature: computeSignature,
  save: saveSlice,
  getCompletionData: getCompletionData,
  impactsCompletion: true,
};
